<template>
    <div>
        <section v-if="preview" class="content-box">
            <a class="pull-right" @click.prevent="gotoForm()">
                <button class="btn btn-small btn-gray">{{ language.main.back }}</button>
            </a>
            <h2 class="lined">{{ language.batches.add.preview }}</h2>

            <div>
                <div style="margin-bottom: 20px;">{{ language.batches.add.preview_description }}</div>
                <div v-if="!fetching && !saving" v-for="(list, date) in trips" style="margin-bottom: 15px;">
                    <strong>{{ date }}</strong><br>
                    <span>{{ list.join(', ') }}</span>
                </div>
                <div v-if="fetching">
                    {{ language.batches.fetching }}
                </div>
                <div v-if="saving">
                    {{ language.batches.saving }}
                </div>
            </div>
            <div>
                <button class="w100 btn btn-success" :disabled="saving" @click="save()">
                    {{ language.main.save }}
                </button>
            </div>
        </section>
        <section v-else class="content-box">
            <a class="pull-right" href="/trips/batches">
                <button class="btn btn-small btn-gray">{{ language.main.back }}</button>
            </a>
            <h2 class="lined">{{ language.batches.add.title }}</h2>

            <div class="form-group" :class="{'has-error': this.errors['name']}">
                <div class="col-md-2 text-right">
                    <label for="name" class="control-label">{{ language.main.name }}</label>
                </div>
                <div class="col-md-10">
                    <input id="name" class="form-control" v-model="form.name" />
                    <small v-for="item in this.errors['name']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['trip_profile']}">
                <div class="col-md-2 text-right">
                    <label for="trip_profile" class="control-label">{{ language.batches.add.trip_profile }}</label>
                </div>
                <div class="col-md-10">
                    <select id="trip_profile" class="form-control" v-model="form.trip_profile">
                        <option v-for="(item, index) in profiles" :key="index" :value="index">{{ item }}</option>
                    </select>
                    <small v-for="item in this.errors['trip_profile']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['date_begin']}">
                <div class="col-md-2 text-right">
                    <label for="date_begin" class="control-label">{{ language.batches.add.date_begin }}</label>
                </div>
                <div class="col-md-10">
                    <flat-pickr
                        v-model="form.date_begin"
                        :config="flatpickrConfig"
                        class="form-control"
                        id="date_begin">
                    </flat-pickr>
                    <small v-for="item in this.errors['date_begin']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['date_end']}">
                <div class="col-md-2 text-right">
                    <label for="date_end" class="control-label">{{ language.batches.add.date_end }}</label>
                </div>
                <div class="col-md-10">
                    <flat-pickr
                        v-model="form.date_end"
                        :config="flatpickrConfig"
                        class="form-control"
                        id="date_end">
                    </flat-pickr>
                    <small v-for="item in this.errors['date_end']" class="text-danger">{{ item }}</small>
                </div>
                <div class="clear-both"></div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['days']}">
                <div class="col-md-2 text-right">
                    <label for="date_end" class="control-label">{{ language.batches.add.weekdays }}</label>
                </div>
                <div class="col-md-10">
                    <div class="btn-group">
                        <label
                            v-for="(value, index) in form.days"
                            :key="index"
                            :for="'date_'+index"
                            class="btn btn-small btn-default"
                            :class="{active: value}"
                        >
                            <input
                                :id="'date_'+index"
                                type="checkbox"
                                class="inline-checkbox"
                                :bind="form.days[index]"
                                @click="form.days[index] = !value"
                            >
                            {{ language.global.weekday[index] }}
                        </label>
                    </div>
                    <small v-for="item in this.errors['days']" class="text-danger">{{ item }}</small>
                </div>
                <div class="clearfix"></div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['hours_begin'] | this.errors['hours_end']}">
                <div class="col-md-2 text-right">
                    <label for="hours_begin" class="control-label">{{ language.batches.add.hours }}</label>
                </div>
                <div class="col-md-10">
                    <label for="daylight_hours">
                        <input type="checkbox" id="daylight_hours" class="inline-checkbox" v-model="form.daylight_hours">
                        {{ language.batches.add.daylight_hours }}
                    </label>
                    <div class="row">
                        <div class="col-md-2">{{ language.main.from }}</div>
                        <div class="col-md-3">
                             <div class="input-group">
                                 <input id="hours_begin" class="form-control" v-model="form.hours_begin" />
                                 <div class="input-group-addon">:00</div>
                             </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-2">{{ language.main.to }}</div>
                        <div class="col-md-3">
                             <div class="input-group">
                                 <input id="hours_end" class="form-control" v-model="form.hours_end" />
                                 <div class="input-group-addon">:00</div>
                             </div>
                        </div>
                    </div>
                    <small v-for="item in this.errors['hours_begin']" class="text-danger">{{ item }}</small>
                    <small v-for="item in this.errors['hours_end']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['price_adult'] | this.errors['price_kid']}">
                <div class="col-md-2 text-right">
                    <label for="price_adult" class="control-label">{{ language.main.price }}</label>
                </div>
                <div class="col-md-10">
                    <label for="price_settings">
                        <input type="checkbox" id="price_settings" class="inline-checkbox" v-model="form.price_settings">
                        {{ language.batches.add.price_settings }}
                    </label>
                    <div class="row" v-if="!form.price_settings">
                        <div class="col-md-2">{{ language.main.price_adult }}</div>
                        <div class="col-md-3">
                             <div class="input-group">
                                 <input id="price_adult" class="form-control" v-model="form.price_adult" />
                                 <div class="input-group-addon">{{ appSettings.valuta }}</div>
                             </div>
                        </div>
                    </div>
                    <div class="row" v-if="!form.price_settings">
                        <div class="col-md-2">{{ language.main.price_kid }}</div>
                        <div class="col-md-3">
                             <div class="input-group">
                                  <input id="price_kid" class="form-control" v-model="form.price_kid" />
                                  <div class="input-group-addon">{{ appSettings.valuta }}</div>
                             </div>
                        </div>
                    </div>
                    <small v-for="item in this.errors['price_adult']" class="text-danger">{{ item }}</small>
                    <small v-for="item in this.errors['price_kid']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['trip_type']}">
                <div class="col-md-2 text-right">
                    <label for="trip_type" class="control-label">{{ language.main.type }}</label>
                </div>
                <div class="col-md-10">
                    <select id="trip_type" class="form-control" v-model="form.trip_type">
                        <option v-for="(item, index) in tripTypes" :key="index" :value="index">{{ item }}</option>
                    </select>
                    <small v-for="item in this.errors['trip_type']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['trip_language']}">
                <div class="col-md-2 text-right">
                    <label for="trip_language" class="control-label">{{ language.main.lang }}</label>
                </div>
                <div class="col-md-10">
                    <select id="trip_language" class="form-control" v-model="form.trip_language">
                        <option v-for="(item, index) in languages" :key="index" :value="index">{{ item }}</option>
                    </select>
                    <small v-for="item in this.errors['trip_language']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['trip_status']}">
                <div class="col-md-2 text-right">
                    <label for="trip_status" class="control-label">{{ language.main.status }}</label>
                </div>
                <div class="col-md-10">
                    <select id="trip_status" class="form-control" v-model="form.trip_status">
                        <option v-for="(item, index) in tripStates" :key="index" :value="index">{{ item }}</option>
                    </select>
                    <small v-for="item in this.errors['trip_status']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group" :class="{'has-error': this.errors['trip_startpos']}">
                <div class="col-md-2 text-right">
                    <label for="trip_startpos" class="control-label">{{ language.trips.start_pos_th }}</label>
                </div>
                <div class="col-md-10">
                    <select id="trip_startpos" class="form-control" v-model="form.trip_startpos">
                        <option v-for="(item, index) in tripStartPositions" :key="index" :value="index">{{ item }}</option>
                    </select>
                    <small v-for="item in this.errors['trip_startpos']" class="text-danger">{{ item }}</small>
                </div>
            </div>

            <div class="form-group">
                <div class="col-md-offset-2 col-md-10">
                    <button class="w100 btn btn-success" @click="gotoPreview()">{{ language.main.preview }}</button>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import {language as flatpickrLanguage } from "@elems/flatpickr-language"


export default {
    components: {
        flatPickr,
    },
    props: {
        template: Object|Array,
        profiles: Object,
        language: Object,
        languages: Object,
    },

    computed: {
        tripStates() {
            return this.language.trips.status
        },

        tripStartPositions() {
            return this.language.trips.start_pos
        },

        tripTypes() {
            return this.language.trips.types
        }
    },

    data() {
        return {
            preview: false,
            fetching: false,
            saving: false,
            trips: {},
            errors: [],
            form: {
                name: '',
                date_begin: '',
                date_end: '',
                daylight_hours: true,
                hours_begin: 8,
                hours_end: 16,
                days: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false,
                },
                price_settings: true,
                price_adult: 0,
                price_kid: 0,
                trip_status: 0,
                trip_type: 1,
                trip_startpos: 1,
                trip_language: 0,
                trip_profile: null,
            },
            flatpickrConfig: {
                dateFormat: 'Y-m-d',
                locale: flatpickrLanguage,
                weekNumbers: true,
                minDate: new Date(),
            },
        }
    },

    mounted() {
        this.loadTemplate()
    },

    methods: {
        gotoForm() {
            this.preview = false
            this.fetching = false
            this.saving = false
            this.trips = {}
        },

        gotoPreview() {
            this.preview = true
            this.fetching = true
            window.axios.post('/trips/batches/preview', this.form)
                .then(({data}) => {
                    this.trips = data
                    this.fetching = false
                })
            .catch(({response}) => {
                this.gotoForm()
                if (response.status === 422) {
                    this.errors = response.data.errors
                }
            })
        },

        save() {
            this.saving = true
            window.axios.post('/trips/batches/save', this.form)
                .then((data) => {
                    window.location = '/trips/batches'
                })
            .catch(error => {
                this.saving = false
                console.error(error)
            })
        },

        loadTemplate() {
            if (this.template.length === 0) {
                return
            }

            this.form = {
                name: this.template.name,
                date_begin: window.moment(this.template.date_begin * 1000).format('YYYY-MM-DD'),
                date_end: window.moment(this.template.date_end * 1000).format('YYYY-MM-DD'),
                daylight_hours: this.template.daylight_hours,
                hours_begin: this.template.hours_begin,
                hours_end: this.template.hours_end,
                days: this.template.days,
                price_settings: this.template.price_settings,
                price_adult: this.template.price_adult,
                price_kid: this.template.price_kid,
                trip_status: this.template.trip_status,
                trip_type: this.template.trip_type,
                trip_startpos: this.template.trip_startpos,
                trip_profile: this.template.trip_profile,
                trip_language: this.template.trip_language,
            }
        },
    }
}
</script>

<style scoped>
.inline-checkbox {
    display: inline-block;
    margin-right: 10px;
}

.btn input[type="checkbox"] {
    display: none;
}

/* Legacy bs override */
select {
    margin-top: 0;
    margin-bottom: 10px;
}
</style>
